import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import { sprintf } from "utils/strings";

import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";

import SchemaDialogForm from "components/Form/SchemaDialogForm"; // adjust path to your actual file
import AdminsTableData from "./data/AdminsTableData";
import { adminFormSchema, adminRecord } from "./data/AdminSchemas";
import { clearTokenAndRedirectToLogin } from "utils/auth";
import { handleApiError } from "utils/apiError";

function Admins() {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(adminRecord);
  const [dialogTitle, setDialogTitle] = useState("Create Admin");
  const [saving, setSaving] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarColor, setSnackBarColor] = useState("error");
  const [snackBarIcon, setSnackBarIcon] = useState("warning");
  const [snackBarTitle, setSnackBarTitle] = useState("Error");
  const [snackErrorMsg, setSnackErrorMsg] = useState("");
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [permissionGroupOptions, setPermissionGroupOptions] = useState([]);
  const [bulkActions, setBulkActions] = useState([
    { key: "select_all", text: "Select All", icon: "select_all", iconColor: "text" },
    { key: "unselect_all", text: "Unselect All", icon: "deselect", iconColor: "text" },
    { key: "delete_selected", text: "Delete", icon: "delete", iconColor: "error" },
  ]);
  const [searchInput, setSearchInput] = useState("");

  const loadPermissionLookups = async () => {
    try {
      const [permsRes, groupsRes] = await Promise.all([
        httpClient.get(`${apiBase}/api/permissions/list`),
        httpClient.get(`${apiBase}/api/permission-groups`),
      ]);

      // Expecting arrays of DTOs from backend
      const perms = permsRes.data || [];
      const groups = groupsRes.data.content || [];

      setPermissionOptions(perms);
      setPermissionGroupOptions(groups);
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    }
  };

  const showSnackBar = (title, type, message) => {
    setSnackBarColor(type === "error" ? "error" : "success");
    setSnackBarIcon(type === "error" ? "warning" : "check_circle");
    setSnackBarTitle(title);
    setSnackErrorMsg(message);
    setSnackBarOpen(true);
    setTimeout(() => setSnackBarOpen(false), 3000);
  };

  const handleToggleCheckbox = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    setSelectedIds([]);
    loadPermissionLookups();
  }, [page]);

  const handleSearchFieldKeyUp = (event) => {
    if (event.key === "Enter") {
      loadAdmins(searchInput);
    }
  };

  const handleSearchOnClick = async () => {
    loadAdmins(searchInput);
  };

  const loadAdmins = async (q) => {
    try {
      setLoading(true);
      let url;
      if (q.length > 0) {
        url = `${apiBase}/api/admins/search-list?page=${page}&size=${rowsPerPage}&q=${q}`;
      } else {
        url = `${apiBase}/api/admins?page=${page}&size=${rowsPerPage}`;
      }
      const res = await httpClient.get(url);

      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);

      // if your backend returns these too, keep them:
      setRowsPerPage(res.data.size || rowsPerPage);
      setPage(res.data.page || page);
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins("");
  }, [page, rowsPerPage]);

  const openCreateDialog = () => {
    setEditingRecord({
      ...adminRecord,
      passwordResetStatus: "require",
      permissions: [],
      permissionGroups: [],
    });
    setDialogTitle("Create Admin");
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecord({
      ...rec,
      password: "",
      permissions: rec.permissions || [],
      permissionGroups: rec.permissionGroups || [],
    });
    setDialogTitle("Edit Admin");
    setDialogOpen(true);
  };

  const onCloseDialog = () => {
    setDialogOpen(false);
  };

  const validateCreatePassword = (data) => {
    // Require password on create only
    const isCreate = !data.id || Number(data.id) === 0;
    if (isCreate && (!data.password || String(data.password).trim().length < 6)) {
      throw new Error("Password is required (min 6 characters) for new admins.");
    }
  };

  const onSubmit = async (data) => {
    validateCreatePassword(data);

    setSaving(true);
    try {
      const payload = { ...data };

      // Transform multiselect arrays -> backend DTO arrays
      /*
      payload.permissions = (payload.permissionIds || []).map((id) => ({ id }));
      payload.permissionGroups = (payload.permissionGroupIds || []).map((id) => ({ id }));

      // remove UI-only fields
      delete payload.permissionIds;
      delete payload.permissionGroupIds;
      */

      const isEdit = payload.id && Number(payload.id) > 0;

      if (isEdit && (!payload.password || String(payload.password).trim() === "")) {
        delete payload.password;
      }

      if (isEdit) {
        await httpClient.put(`${apiBase}/api/admins/${payload.id}`, payload);
        showSnackBar("Success", "success", "Admin updated");
      } else {
        await httpClient.post(`${apiBase}/api/admins`, payload);
        showSnackBar("Success", "success", "Admin created");
      }

      setDialogOpen(false);
      await loadAdmins("");
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rec) => {
    setItemToDelete(rec);
    setConfirmationDialogMessage(sprintf(t("confirmation.toDeleteItem"), [`admin (${rec.name})`]));
    setConfirmationDialogOpen(true);
  };

  const onDeleteConfirmed = async () => {
    try {
      const auth = localStorage.getItem("auth");
      let parsedAuth = null;
      if (auth) {
        try {
          parsedAuth = JSON.parse(auth);
        } catch {
          // ignore
          return;
        }
      }
      let res;
      if (Array.isArray(itemToDelete)) {
        res = await httpClient.post(
          `${apiBase}/api/admins/bulk-delete`,
          { ids: itemToDelete },
          {
            headers: {
              Authorization: `Bearer ${parsedAuth.token}`,
            },
          }
        );
      } else {
        res = await httpClient.delete(`${apiBase}/api/admins/${itemToDelete.id}`, {
          headers: {
            Authorization: `Bearer ${parsedAuth.token}`,
          },
        });
      }
      if (res.status == 204) {
        showSnackBar(
          t("successMsg.operationTitleSuccess"),
          "success",
          sprintf(t("successMsg.itemDeleteSuccessfully"), ["Admin(s)"])
        );
        await loadAdmins("");
      }
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setConfirmOpen(false);
      setConfirmationDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteBulk = async (ids) => {
    setItemToDelete(ids);
    setConfirmationDialogMessage(t("confirmation.toDeleteAdmin"));
    setConfirmationDialogOpen(true);
  };

  const actionsMenuItemClicked = async (key) => {
    if (key.includes("Delete")) {
      handleDeleteBulk(selectedIds);
      return;
    } else if (key.includes("Select All")) {
      handleSelectAll();
    } else if (key.includes("Unselect All")) {
      handleUnSelectAll();
    }
  };

  const handleSelectAll = () => {
    for (let i = 0; i < items.length; i++) {
      if (selectedIds.includes(items[i].id)) {
        continue;
      }
      handleToggleCheckbox(items[i].id);
    }
  };

  const handleUnSelectAll = () => {
    setSelectedIds([]);
  };

  const renderSB = (
    <MDSnackbar
      color={snackBarColor}
      icon={snackBarIcon}
      title={snackBarTitle}
      content={snackErrorMsg}
      dateTime=""
      open={snackBarOpen}
      onClose={() => setSnackBarOpen(false)}
      close={() => setSnackBarOpen(false)}
      bgWhite
    />
  );

  const selectOptionsMap = {
    permissions: permissionOptions,
    permissionGroups: permissionGroupOptions,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <MDButton variant="gradient" color="info" size="small" onClick={openCreateDialog}>
                Create Admin <Icon>add</Icon>
              </MDButton>
              <MDBox alignItems="right" display="flex">
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDInput
                    size="small"
                    label={t("search.searchAdmins")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyUp={handleSearchFieldKeyUp}
                  />
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={handleSearchOnClick}
                  >
                    {t("search.go")}
                  </MDButton>
                </MDBox>
                <CustomMenuButton
                  items={bulkActions}
                  onItemClicked={(item) => actionsMenuItemClicked(item)}
                  buttonTitle={"Actions"}
                />
              </MDBox>
            </MDBox>

            <MDBox px={3} pb={3}>
              {loading ? (
                <MDTypography variant="button">Loading...</MDTypography>
              ) : items.length === 0 ? (
                <MDTypography variant="button">No admins found.</MDTypography>
              ) : (
                <div>
                  <AdminsTableData
                    data={items}
                    selectedIds={selectedIds}
                    onToggle={handleToggleCheckbox}
                    onEdit={openEditDialog}
                    onDelete={handleDelete}
                  />

                  <CustomTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalElements}
                    onPageChange={(newPage) => setPage(newPage)}
                    onRowsPerPageChange={(n) => {
                      setRowsPerPage(n);
                      setPage(0);
                    }}
                  />
                </div>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      {renderSB}

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title={t("confirmation.confirm")}
        message={confirmationDialogMessage}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <SchemaDialogForm
        open={dialogOpen}
        title={dialogTitle}
        schema={adminFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={onCloseDialog}
        onSubmit={onSubmit}
        deps={selectOptionsMap}
      />
    </DashboardLayout>
  );
}

export default Admins;
